import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Container from '../../components/layout/Container/Container';
import { Row, Col } from '../../components/layout/Grid/Grid';
import Card, { CardBody } from '../../components/ui/Card/Card';
import Badge from '../../components/ui/Badge/Badge';
import cvData from '../../assets/data/cv-data';
import './Portfolio.css';

const Portfolio = () => {
  const [filter, setFilter] = useState('all');
  const [selectedProject, setSelectedProject] = useState(null);

  const categories = [
    { id: 'all', label: 'All Projects', icon: 'fas fa-th-large' },
    { id: 'web', label: 'Web Development', icon: 'fas fa-globe' },
    { id: 'mobile', label: 'Mobile Apps', icon: 'fas fa-mobile-alt' },
    { id: 'design', label: 'UI/UX Design', icon: 'fas fa-paint-brush' }
  ];

  const filteredProjects = filter === 'all' 
    ? cvData.projects 
    : cvData.projects.filter(p => p.category === filter);

  return (
    <div className="portfolio-page">
      {/* Hero Section */}
      <section className="portfolio-hero">
        <Container>
          <Row>
            <Col lg={8} className="mx-auto text-center">
              <h1 className="portfolio-hero-title">
                My <span className="gradient-text">Portfolio</span>
              </h1>
              <p className="portfolio-hero-subtitle">
                Explore my latest work and creative projects. Each project represents 
                a unique challenge and solution.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Filter Section */}
      <section className="portfolio-filter-section">
        <Container>
          <div className="filter-wrapper">
            <div className="filter-buttons">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  className={`filter-btn ${filter === cat.id ? 'active' : ''}`}
                  onClick={() => setFilter(cat.id)}
                >
                  <i className={cat.icon}></i>
                  <span>{cat.label}</span>
                </button>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* Projects Grid */}
      <section className="portfolio-grid-section">
        <Container>
          <Row>
            {filteredProjects.map((project, index) => (
              <Col lg={4} md={6} key={project.id}>
                <div 
                  className="portfolio-item"
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                >
                  <Card className="portfolio-card">
                    <div className="portfolio-card-inner">
                      <div className="portfolio-image-wrapper">
                        <img 
                          src={project.image} 
                          alt={project.title}
                          className="portfolio-image"
                        />
                        <div className="portfolio-overlay">
                          <div className="portfolio-actions">
                            <button 
                              className="action-btn view-btn"
                              onClick={() => setSelectedProject(project)}
                            >
                              <i className="fas fa-search-plus"></i>
                            </button>
                            <a 
                              href={project.liveUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="action-btn live-btn"
                            >
                              <i className="fas fa-external-link-alt"></i>
                            </a>
                            <a 
                              href={project.githubUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="action-btn github-btn"
                            >
                              <i className="fab fa-github"></i>
                            </a>
                          </div>
                        </div>
                        {project.featured && (
                          <div className="featured-badge">
                            <i className="fas fa-star"></i>
                            <span>Featured</span>
                          </div>
                        )}
                      </div>
                      <CardBody>
                        <div className="portfolio-category">
                          <Badge variant="primary">{project.category}</Badge>
                        </div>
                        <h3 className="portfolio-title">{project.title}</h3>
                        <p className="portfolio-description">{project.description}</p>
                        <div className="portfolio-technologies">
                          {project.technologies.slice(0, 3).map(tech => (
                            <span key={tech} className="tech-tag">{tech}</span>
                          ))}
                          {project.technologies.length > 3 && (
                            <span className="tech-tag more">+{project.technologies.length - 3}</span>
                          )}
                        </div>
                        <div className="portfolio-links">
                          <Link to={`/project/${project.id}`} className="details-link">
                            View Details
                            <i className="fas fa-arrow-right"></i>
                          </Link>
                        </div>
                      </CardBody>
                    </div>
                  </Card>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Project Modal */}
      {selectedProject && (
        <div className="project-modal" onClick={() => setSelectedProject(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedProject(null)}>
              <i className="fas fa-times"></i>
            </button>
            <div className="modal-body">
              <div className="modal-image">
                <img src={selectedProject.image} alt={selectedProject.title} />
              </div>
              <div className="modal-details">
                <h2 className="modal-title">{selectedProject.title}</h2>
                <p className="modal-description">{selectedProject.description}</p>
                <div className="modal-tech-stack">
                  <h3>Technologies Used:</h3>
                  <div className="tech-tags">
                    {selectedProject.technologies.map(tech => (
                      <Badge key={tech} variant="primary">{tech}</Badge>
                    ))}
                  </div>
                </div>
                <div className="modal-links">
                  <a href={selectedProject.liveUrl} target="_blank" rel="noopener noreferrer" 
                     className="btn btn-primary">
                    <i className="fas fa-external-link-alt me-2"></i>
                    Live Demo
                  </a>
                  <a href={selectedProject.githubUrl} target="_blank" rel="noopener noreferrer"
                     className="btn btn-outline-primary">
                    <i className="fab fa-github me-2"></i>
                    Source Code
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Section */}
      <section className="portfolio-stats">
        <Container>
          <Row>
            <Col md={3} sm={6}>
              <div className="stat-box">
                <i className="fas fa-project-diagram"></i>
                <h3>20+</h3>
                <p>Projects Completed</p>
              </div>
            </Col>
            <Col md={3} sm={6}>
              <div className="stat-box">
                <i className="fas fa-users"></i>
                <h3>15+</h3>
                <p>Happy Clients</p>
              </div>
            </Col>
            <Col md={3} sm={6}>
              <div className="stat-box">
                <i className="fas fa-code"></i>
                <h3>50K+</h3>
                <p>Lines of Code</p>
              </div>
            </Col>
            <Col md={3} sm={6}>
              <div className="stat-box">
                <i className="fas fa-trophy"></i>
                <h3>5+</h3>
                <p>Awards Won</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default Portfolio;
